import { Box, Flex, Text } from '@chakra-ui/react';
import { Button } from '@morressier/alembic';
import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import ViewVariables from './ViewVariables';
import BrowseTemplates from './BrowseTemplates';
import ConfirmTemplateModal from './ConfirmTemplateModal';
import { EmailTemplateDoc } from '@publishing-engine/domain/models/email-templates';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import EditEmailTemplate, { IEmailFormType } from './EditEmailTemplate';
import EditUserEmailTemplate from './EditUserEmailTemplate';
import { getOrgId } from '../../pages/Organization/Workspace/SubmissionContexts/Users/userHook';
import BrowseTemplatesMenu from './BrowseTemplatesMenu';

interface Props {
  emailTemplates: EmailTemplateDoc[] | undefined;
  adHocEmail?: boolean;
  removeBrowseTemplates?: boolean;
  emails: string[];
  attachments?: ISubmissionFile[];
  reviewDetails?: {
    reviewId: string;
    reviewRequestId?: string;
  };
  setIsEmailTemplatePopulated?: (value: boolean) => void;
}

export type EmailContentForm = {
  emailContent: IEmailFormType;
};

const ComposeEmail = ({
  emailTemplates,
  adHocEmail = false,
  removeBrowseTemplates = false,
  emails,
  attachments = [],
  reviewDetails,
  setIsEmailTemplatePopulated
}: Props) => {
  const organizationId = getOrgId();
  const {
    getValues,
    setValue,
    reset,
    formState: { isDirty }
  } = useFormContext<EmailContentForm>();

  // Actual selected template that's populating the email form
  const [userSelectedIndex, setUserSelectedIndex] = useState<number | null>(
    null
  );
  const [templateName, setTemplateName] = useState<string>('Adhoc Email');
  // Template selected in the browse templates modal, but not yet selected by the user
  const [pendingTemplateIndex, setPendingTemplateIndex] = useState<
    number | null
  >(null);
  const [initialFormReady, setInitialFormReady] = useState(false);
  const [openBrowseTemplates, setOpenBrowseTemplates] = useState(false);
  const [openViewVariables, setOpenViewVariables] = useState(false);
  const [openConfirmTemplateModal, setOpenConfirmTemplateModal] =
    useState(false);

  const eventName =
    userSelectedIndex !== null && emailTemplates
      ? emailTemplates[userSelectedIndex]?.event
      : '';

  useEffect(() => {
    if (!emailTemplates || emailTemplates.length === 0) return;
    if (initialFormReady) return;
    const defaultIndex = emailTemplates.findIndex(template => template.default);
    const selectedIndex = defaultIndex === -1 ? 0 : defaultIndex;
    const template = emailTemplates[selectedIndex];
    if (!template) return; // ← type guard

    if (!removeBrowseTemplates) {
      setUserSelectedIndex(selectedIndex);
      setTemplateName(template.name);
      setIsEmailTemplatePopulated?.(true);

      reset({
        emailContent: {
          recipients: {
            to: emails.map(email => ({ text: email })),
            cc: template.recipients.cc?.map(e => ({ text: e })) ?? [],
            bcc: template.recipients.bcc?.map(e => ({ text: e })) ?? []
          },
          subject: template.subject,
          body: template.body,
          attachments
        }
      });
    } else {
      const currentTo = getValues('emailContent.recipients.to');
      setIsEmailTemplatePopulated?.(false);

      const emailFormDefaults: EmailContentForm = {
        emailContent: {
          body: '',
          subject: '',
          recipients: { to: currentTo ?? [] },
          attachments: []
        }
      };

      reset(emailFormDefaults);
    }

    setInitialFormReady(true);
  }, [
    emailTemplates,
    adHocEmail,
    attachments,
    initialFormReady,
    reset,
    removeBrowseTemplates,
    getValues
  ]);

  useEffect(() => {
    setValue(
      'emailContent.recipients.to',
      emails.map(email => ({ text: email }))
    );
  }, [emails]);

  const applyTemplate = (index: number) => {
    const template = emailTemplates?.[index];
    if (!template) return;
    setTemplateName(template.name);
    reset({
      emailContent: {
        recipients: {
          to: emails?.map(email => ({ text: email })),
          cc:
            template.recipients.cc?.map(email => ({
              text: email
            })) ?? [],
          bcc:
            template.recipients.bcc?.map(email => ({
              text: email
            })) ?? []
        },
        subject: template.subject,
        body: template.body,
        attachments
      }
    });
  };

  const handleSelectTemplate = (index: number) => {
    if (index === userSelectedIndex) {
      setOpenBrowseTemplates(false);
      return;
    }
    if (userSelectedIndex === null || !isDirty) {
      applyTemplate(index);
      setUserSelectedIndex(index);
      setOpenBrowseTemplates(false);
    } else {
      setPendingTemplateIndex(index);
      setOpenBrowseTemplates(false);
      setOpenConfirmTemplateModal(true);
    }
  };

  return (
    <Box backgroundColor="surfacePrimaryFill">
      <Flex flexDirection="column" gap={2}>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          paddingX={4}
          paddingTop={4}
        >
          <Text textStyle="headingGroup">
            {adHocEmail ? templateName || 'Blank Template' : templateName}
          </Text>
          <Flex gap={4}>
            <Button
              variant="secondary"
              onClick={() => setOpenViewVariables(true)}
            >
              View Variables
            </Button>
            {!removeBrowseTemplates &&
              emailTemplates &&
              emailTemplates.length > 1 && (
                <BrowseTemplatesMenu
                  templates={emailTemplates}
                  selectedIndex={userSelectedIndex}
                  onSelectTemplate={index => {
                    setUserSelectedIndex(index);
                    applyTemplate(index);
                  }}
                  onOpenDetailView={() => setOpenBrowseTemplates(true)}
                />
              )}
          </Flex>
        </Flex>

        {!removeBrowseTemplates && (
          <EditEmailTemplate
            isCustomTemplate={adHocEmail}
            showTestEmail
            eventName={eventName}
            reviewDetails={reviewDetails}
          />
        )}
        {removeBrowseTemplates && (
          <EditUserEmailTemplate
            organizationId={organizationId}
            showTestEmail
          />
        )}
      </Flex>
      {openViewVariables && (
        <ViewVariables onClose={() => setOpenViewVariables(false)} />
      )}
      {openBrowseTemplates && emailTemplates?.length && (
        <BrowseTemplates
          templates={emailTemplates}
          onSelectTemplate={handleSelectTemplate}
          onClose={() => setOpenBrowseTemplates(false)}
          defaultIndex={userSelectedIndex}
          isCustomTemplate={adHocEmail}
        />
      )}
      {openConfirmTemplateModal && (
        <ConfirmTemplateModal
          onClose={() => {
            setOpenConfirmTemplateModal(false);
            setOpenBrowseTemplates(true);
            setPendingTemplateIndex(null);
          }}
          onConfirm={() => {
            if (pendingTemplateIndex === null) return;
            applyTemplate(pendingTemplateIndex);
            setUserSelectedIndex(pendingTemplateIndex);
            setOpenConfirmTemplateModal(false);
            setPendingTemplateIndex(null);
          }}
        />
      )}
    </Box>
  );
};

export default ComposeEmail;
